import { 
  Monitor, 
  Smartphone, 
  Server, 
  Printer,
  Search,
  Filter,
  Plus,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

export interface Asset {
  id: string;
  name: string;
  type: 'desktop' | 'laptop' | 'server' | 'printer' | 'mobile';
  status: 'active' | 'inactive' | 'maintenance';
  assignedTo: string | null;
  department: string;
  lastSeen: string;
  os: string;
  ip: string;
}

const typeIcons = {
  desktop: Monitor,
  laptop: Monitor,
  server: Server,
  printer: Printer,
  mobile: Smartphone,
};

const statusConfig = {
  active: { label: 'Active', icon: CheckCircle, className: 'bg-success/20 text-success border-success/30' },
  inactive: { label: 'Inactive', icon: XCircle, className: 'bg-muted text-muted-foreground border-border' },
  maintenance: { label: 'Maintenance', icon: AlertCircle, className: 'bg-warning/20 text-warning border-warning/30' },
};

interface AssetListProps {
  assets: Asset[];
  selectedAssetId: string | null;
  onSelectAsset: (id: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onAddAsset?: () => void;
}

export function AssetList({ 
  assets, 
  selectedAssetId, 
  onSelectAsset, 
  searchQuery, 
  onSearchChange,
  onAddAsset 
}: AssetListProps) {
  return (
    <div className="w-96 flex-shrink-0 border-r border-border flex flex-col bg-card">
      <div className="h-12 border-b border-border flex items-center justify-between px-4">
        <h2 className="text-sm font-semibold text-foreground">ASSETS ({assets.length})</h2>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <Filter className="h-4 w-4" />
          </Button>
          <Button size="sm" className="h-8 gap-1" onClick={onAddAsset}>
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </div>
      </div>

      <div className="p-3 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search assets..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 h-9 bg-muted"
          />
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="divide-y divide-border">
          {assets.map((asset) => {
            const Icon = typeIcons[asset.type];
            const status = statusConfig[asset.status];
            const StatusIcon = status.icon;

            return (
              <button
                key={asset.id}
                onClick={() => onSelectAsset(asset.id)}
                className={cn(
                  "w-full p-4 text-left transition-colors hover:bg-muted/50",
                  selectedAssetId === asset.id && "bg-muted"
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center">
                    <Icon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{asset.name}</span>
                      <StatusIcon className={cn("h-3.5 w-3.5", status.className.split(' ')[1])} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{asset.department}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
